import React, { useEffect, useMemo, useState } from 'react';
import { Colors as C } from '../../constants/Colors.js';
import { SELLER_TO_PARTNER_MODES, sellerModesToArray } from '../constants/partnerLogistics.js';
import {
  fetchPartnersForListingCoverageModes,
  formatPartnerPlaceLabel,
} from '../../lib/listingPartnerLogisticsService.js';

function ModeCheckboxGroup({ modes, onChange, label }) {
  const toggle = value => {
    const next = { ...modes, [value]: !modes[value] };
    if (!next.physical && !next.shipping) return;
    onChange(next);
  };

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginBottom: 6 }}>{label}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {SELLER_TO_PARTNER_MODES.map(m => {
          const sel = !!modes[m.value];
          return (
            <label
              key={m.value}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                padding: '8px 10px',
                borderRadius: 8,
                border: `0.5px solid ${sel ? 'rgba(200,168,75,0.5)' : 'rgba(255,255,255,0.1)'}`,
                background: sel ? 'rgba(200,168,75,0.08)' : 'rgba(255,255,255,0.03)',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={sel}
                onChange={() => toggle(m.value)}
                style={{ marginTop: 2, accentColor: C.gold }}
              />
              <span>
                <span style={{ fontSize: 11, fontWeight: 600, color: sel ? C.gold : '#fff' }}>{m.label}</span>
                <span style={{ display: 'block', fontSize: 9.5, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
                  {m.desc}
                </span>
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function PartnerCheckList({ partners, loading, selectedIds, onToggle }) {
  const byCity = useMemo(() => {
    const map = new Map();
    for (const p of partners) {
      const city = String(p.city || 'Sin ciudad').trim();
      if (!map.has(city)) map.set(city, []);
      map.get(city).push(p);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0], 'es'));
  }, [partners]);

  if (loading) {
    return <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', padding: '8px 0' }}>Cargando partners…</div>;
  }
  if (!partners.length) {
    return (
      <div style={{ fontSize: 10, color: C.warn, padding: '8px 0', lineHeight: 1.45 }}>
        No hay partners disponibles con esas modalidades. Prueba otra combinación o quita esta opción de envío.
      </div>
    );
  }

  return (
    <div style={{ maxHeight: 220, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
      {byCity.map(([city, list]) => (
        <div key={city}>
          <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', marginBottom: 4, textTransform: 'uppercase' }}>
            {city}
          </div>
          {list.map(p => {
            const id = p.partner_id;
            const sel = selectedIds.includes(id);
            return (
              <label
                key={id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  padding: '7px 8px',
                  borderRadius: 8,
                  marginBottom: 4,
                  border: `0.5px solid ${sel ? 'rgba(200,168,75,0.35)' : 'rgba(255,255,255,0.08)'}`,
                  background: sel ? 'rgba(200,168,75,0.06)' : 'transparent',
                  cursor: 'pointer',
                  fontSize: 11,
                  color: 'rgba(255,255,255,0.8)',
                }}
              >
                <input
                  type="checkbox"
                  checked={sel}
                  onChange={() => onToggle(id)}
                  style={{ marginTop: 2, accentColor: C.gold }}
                />
                <span>
                  {formatPartnerPlaceLabel(p)}
                  {p.address ? (
                    <span style={{ display: 'block', fontSize: 9, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>
                      {p.address}
                    </span>
                  ) : null}
                </span>
              </label>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export default function PublicarPartnerCoverage({
  showPickup,
  showStore,
  modesPickup,
  modesStore,
  onModesPickupChange,
  onModesStoreChange,
  pickupIds,
  storeIds,
  onTogglePickup,
  onToggleStore,
}) {
  const [pickupPartners, setPickupPartners] = useState([]);
  const [storePartners, setStorePartners] = useState([]);
  const [loadingPickup, setLoadingPickup] = useState(false);
  const [loadingStore, setLoadingStore] = useState(false);

  const pickupModesArr = useMemo(() => sellerModesToArray(modesPickup), [modesPickup]);
  const storeModesArr = useMemo(() => sellerModesToArray(modesStore), [modesStore]);
  const pickupModesKey = pickupModesArr.join(',');
  const storeModesKey = storeModesArr.join(',');

  useEffect(() => {
    if (!showPickup) {
      setPickupPartners([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingPickup(true);
      const { partners } = await fetchPartnersForListingCoverageModes('pickup', pickupModesArr);
      if (!cancelled) {
        setPickupPartners(partners);
        setLoadingPickup(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showPickup, pickupModesKey, pickupModesArr]);

  useEffect(() => {
    if (!showStore) {
      setStorePartners([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingStore(true);
      const { partners } = await fetchPartnersForListingCoverageModes('store', storeModesArr);
      if (!cancelled) {
        setStorePartners(partners);
        setLoadingStore(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showStore, storeModesKey, storeModesArr]);

  if (!showPickup && !showStore) return null;

  return (
    <div
      style={{
        marginBottom: 16,
        padding: '12px 12px 4px',
        borderRadius: 10,
        border: '0.5px solid rgba(200,168,75,0.2)',
        background: 'rgba(200,168,75,0.04)',
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 600, color: C.gold, marginBottom: 8 }}>Red DreamVinyl (partners)</div>
      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.42)', marginBottom: 12, lineHeight: 1.45 }}>
        Marca cómo podrías entregar el disco al partner (puedes elegir ambas). Cuando haya una venta, decidirás si
        llevas el disco en persona o lo envías. El comprador solo podrá elegir entre los partners que marques aquí.
      </div>

      {showPickup ? (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: '#fff', fontWeight: 500, marginBottom: 8 }}>Punto de recogida</div>
          <ModeCheckboxGroup
            modes={modesPickup}
            onChange={onModesPickupChange}
            label="Formas de entrega al punto (puedes marcar ambas)"
          />
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginBottom: 6 }}>
            Puntos disponibles ({pickupIds.length} seleccionados)
          </div>
          <PartnerCheckList
            partners={pickupPartners}
            loading={loadingPickup}
            selectedIds={pickupIds}
            onToggle={onTogglePickup}
          />
        </div>
      ) : null}

      {showStore ? (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: '#fff', fontWeight: 500, marginBottom: 8 }}>
            Partner verificador (tienda)
          </div>
          <ModeCheckboxGroup
            modes={modesStore}
            onChange={onModesStoreChange}
            label="Formas de entrega a la tienda (puedes marcar ambas)"
          />
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginBottom: 6 }}>
            Tiendas disponibles ({storeIds.length} seleccionadas)
          </div>
          <PartnerCheckList
            partners={storePartners}
            loading={loadingStore}
            selectedIds={storeIds}
            onToggle={onToggleStore}
          />
        </div>
      ) : null}
    </div>
  );
}
